import React from 'react';
import { Phone, Mail, Copy, Check } from 'lucide-react';
import type { useApplicationDetail } from '../../hooks/useApplicationDetail';

interface Props { h: ReturnType<typeof useApplicationDetail>; serviceType?: string; }

const PUP_LOAN_PRODUCTS = new Set(['pup_grant', 'wsparcie_na_starcie', 'pozyczki_rozwojowe', 'czyste_powietrze']);
const PESEL_PRODUCTS = new Set(['pup_grant', 'wsparcie_na_starcie', 'pozyczki_rozwojowe', 'czyste_powietrze', 'turnusy', 'kfs', 'zus', 'bhp']);

const CopyBtn: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handleCopy} className="ml-2 text-gray-400 hover:text-blue-600 transition-colors" title="Kopiuj">
      {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
    </button>
  );
};

export const PersonalDataSection: React.FC<Props> = ({ h, serviceType }) => {
  const { application, editMode, editedData } = h;
  if (!application) return null;
  const st = serviceType || application.service_type || '';
  const showPesel = PESEL_PRODUCTS.has(st);
  const showMarital = PUP_LOAN_PRODUCTS.has(st);
  const showAddress = PUP_LOAN_PRODUCTS.has(st) || st === 'turnusy';

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center">
          Dane Osobowe
          {editMode && <span className="ml-3 text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">Tryb edycji</span>}
        </h2>
        {!editMode && application.created_at && (
          <span className="text-xs text-gray-400">Złożono: {new Date(application.created_at).toLocaleDateString('pl-PL')}</span>
        )}
      </div>
      {!editMode ? (
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Imię</p>
            <p className="font-medium">{application.first_name}</p>
          </div>
          <div>
            <p className="text-gray-600">Nazwisko</p>
            <p className="font-medium">{application.last_name}</p>
          </div>
          <div>
            <p className="text-gray-600">Telefon</p>
            <p className="font-medium flex items-center">
              {application.phone ? (
                <>
                  <Phone size={14} className="mr-1.5 text-green-600" />
                  <a href={`tel:${application.phone}`} className="text-blue-600 hover:underline">{application.phone}</a>
                  <CopyBtn text={application.phone} />
                </>
              ) : 'Nie podano'}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Email</p>
            <p className="font-medium flex items-center">
              {application.email ? (
                <>
                  <Mail size={14} className="mr-1.5 text-blue-600" />
                  <a href={`mailto:${application.email}`} className="text-blue-600 hover:underline truncate">{application.email}</a>
                  <CopyBtn text={application.email} />
                </>
              ) : 'Nie podano'}
            </p>
          </div>
          {showPesel && (
            <div>
              <p className="text-gray-600">PESEL</p>
              <p className="font-medium flex items-center">
                {application.pesel || 'Nie podano'}
                {application.pesel && <CopyBtn text={application.pesel} />}
              </p>
            </div>
          )}
          {showMarital && (
            <div>
              <p className="text-gray-600">Stan cywilny</p>
              <p className="font-medium">{application.marital_status || 'Nie podano'}</p>
            </div>
          )}
          {showAddress && (
            <div className="col-span-2">
              <p className="text-gray-600">Adres zamieszkania</p>
              <p className="font-medium flex items-center">
                {application.residence_address || 'Nie podano'}
                {application.residence_address && <CopyBtn text={application.residence_address} />}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Imię</label>
            <input type="text" value={editedData.first_name || ''} onChange={(e) => h.handleEditFieldChange('first_name', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nazwisko</label>
            <input type="text" value={editedData.last_name || ''} onChange={(e) => h.handleEditFieldChange('last_name', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
            <input type="tel" value={editedData.phone || ''} onChange={(e) => h.handleEditFieldChange('phone', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={editedData.email || ''} onChange={(e) => h.handleEditFieldChange('email', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          {showPesel && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PESEL</label>
              <input type="text" value={editedData.pesel || ''} onChange={(e) => h.handleEditFieldChange('pesel', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" maxLength={11} />
            </div>
          )}
          {showMarital && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stan cywilny</label>
              <select value={editedData.marital_status || ''} onChange={(e) => h.handleEditFieldChange('marital_status', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="">Wybierz...</option>
                <option value="single">Kawaler/Panna</option>
                <option value="married">Żonaty/Zamężna</option>
                <option value="divorced">Rozwiedziony/Rozwiedziona</option>
                <option value="widowed">Wdowiec/Wdowa</option>
              </select>
            </div>
          )}
          {showAddress && (
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Adres zamieszkania</label>
              <input type="text" value={editedData.residence_address || ''} onChange={(e) => h.handleEditFieldChange('residence_address', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="ul. Przykładowa 123/45, 31-000 Kraków" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
